import React from "react";
import { useTranslation } from "react-i18next";
import { getIcon, getServiceLabel } from "@/configs/services";
import ProviderLogo, { ProviderLogoProps } from "./ProviderLogo";

export type ServiceLogoProps = Omit<
  ProviderLogoProps,
  "src" | "icon" | "label"
> & {
  /** An `AccountTypes` value — the data-source service whose mark to show. */
  type?: string;
  /**
   * Name the service for assistive tech. Set it when the text beside the logo
   * is not the service name (an account, template or object name), so the
   * service the logo identifies is still announced.
   */
  labelled?: boolean;
  /**
   * A generic mark to show instead of nothing when there is no type — e.g. a
   * category icon, so a list of mixed rows still lines up.
   */
  fallbackIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

/**
 * The service-catalogue mark for a data-source `type`, through `ProviderLogo`
 * so it stays legible in dark mode. An unknown type shows the catalogue's
 * "unregistered" mark; a missing type renders `fallbackIcon`, or nothing.
 */
const ServiceLogo: React.FC<ServiceLogoProps> = ({
  type,
  labelled = false,
  fallbackIcon,
  ...rest
}) => {
  const { t } = useTranslation();
  const src = getIcon(type, 3);
  if (!type || !src) {
    return fallbackIcon ? <ProviderLogo {...rest} icon={fallbackIcon} /> : null;
  }
  return (
    <ProviderLogo
      {...rest}
      src={src}
      label={labelled ? getServiceLabel(t, type) : undefined}
    />
  );
};

export default ServiceLogo;
